import { soldDateLabel, soldFactsLine, soldPriceLabel, type SoldFigures } from '../soldLabels';

/**
 * The /sold page's live list: the site's published sold pages as cards, newest close first, with
 * paging. One component for BOTH hosts — the tenant route at the page's `automatic_content` block
 * (or under its built-in header) and the Studio in the same spot, from the same GET /sold — so the
 * editor shows the homes visitors see. Framework-agnostic (plain <a>).
 */
export interface SoldCardData extends SoldFigures {
  key: string;
  path: string;
  address: string;
  city: string | null;
  close_date: string | null;
  dom: number | null;
  photo_urls: string[];
}

export function SoldCard({ sold }: { sold: SoldCardData }) {
  const price = soldPriceLabel(sold);
  const when = soldDateLabel(sold.close_date);
  const facts = soldFactsLine(sold);
  const photo = sold.photo_urls?.[0];
  return (
    <a href={sold.path} className="auto-list-card blk-card group block rounded-2xl border border-gray-200 bg-white overflow-hidden hover:shadow-md transition-shadow" data-testid="sold-card" data-site-ink="">
      <div className="relative aspect-[4/3] bg-gray-100">
        {photo ? (
          <img src={photo} alt={sold.address} className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-sm text-gray-400">No photo</div>
        )}
        <span className="absolute left-3 top-3 rounded-full bg-gray-900/85 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">Sold</span>
      </div>
      <div className="p-4">
        {price ? <p className="text-lg font-bold text-gray-900">{price}</p> : <p className="text-sm font-semibold text-gray-700">Sold price not disclosed</p>}
        <p className="mt-1 text-sm text-gray-800 group-hover:underline">{sold.address}{sold.city ? `, ${sold.city}` : ''}</p>
        {facts ? <p className="mt-1 text-xs text-gray-500">{facts}</p> : null}
        <p className="mt-2 text-xs text-gray-500">
          {when ? `Closed ${when}` : 'Recently closed'}
          {sold.dom != null ? ` · ${sold.dom} days on market` : ''}
        </p>
      </div>
    </a>
  );
}

export function SoldCardList({ sold, page, totalPages }: { sold: SoldCardData[]; page: number; totalPages: number }) {
  return (
    <>
      {sold.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-10 text-center text-gray-500" data-testid="sold-empty">
          No recent sales to show yet. Check back soon.
        </div>
      ) : (
        <div className="auto-list-grid grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {sold.map((s) => <SoldCard key={s.key} sold={s} />)}
        </div>
      )}

      {totalPages > 1 ? (
        <nav className="mt-10 flex items-center justify-center gap-4 text-sm" aria-label="Pagination">
          {page > 1 ? <a href={page === 2 ? '/sold' : `/sold?page=${page - 1}`} className="rounded-lg border border-gray-300 px-4 py-2 hover:bg-gray-50">← Newer</a> : <span />}
          <span className="text-gray-500">Page {page} of {totalPages}</span>
          {page < totalPages ? <a href={`/sold?page=${page + 1}`} className="rounded-lg border border-gray-300 px-4 py-2 hover:bg-gray-50">Older →</a> : <span />}
        </nav>
      ) : null}
    </>
  );
}
